// Note: we have to merge the progress and the progress circle component, and based on type we can render whether it is a progress bar or a progress circle component. For a quick release, we are creating a separate component for the progress circle.

import { cn } from '@commenda/utils';
import React from 'react';

type ProgressCircleVariant = 'success' | 'info' | 'warning';

export interface ProgressCircleProps {
  value: number;
  variant?: ProgressCircleVariant;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const TRACK_COLOR = 'var(--color-utility-gray-200)';

const VARIANT_STROKE_COLOR: Record<ProgressCircleVariant, string> = {
  success: 'var(--color-fg-success)',
  info: 'var(--color-fg-info)',
  warning: 'var(--color-fg-warning)',
};

const ProgressCircle = React.forwardRef<SVGSVGElement, ProgressCircleProps>(
  (
    { value, variant = 'success', size = 16, strokeWidth = 4, className },
    ref
  ) => {
    const clampedValue = Math.min(100, Math.max(0, value));
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - clampedValue / 100);
    const center = size / 2;

    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={cn('shrink-0', className)}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${clampedValue}% complete`}
      >
        {/* Track (background) circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={TRACK_COLOR}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={VARIANT_STROKE_COLOR[variant]}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>
    );
  }
);

ProgressCircle.displayName = 'ProgressCircle';

export { ProgressCircle };
